import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";

const services = [
  {
    icon: "🏠",
    title: "Residential Framing",
    description: "Complete framing solutions for new homes, additions, and renovations. From foundation to roof, we handle every aspect of residential framing with precision and care.",
    features: [
      "Custom home framing",
      "Room additions",
      "Garage framing",
      "Deck and porch construction",
      "Structural modifications"
    ],
    startingPrice: "$15,000",
    image: "/images/residential-framing.jpg"
  },
  {
    icon: "🏢",
    title: "Commercial Framing",
    description: "Large-scale commercial framing projects including office buildings, retail spaces, warehouses, and multi-story structures.",
    features: [
      "Office building framing",
      "Retail space construction",
      "Warehouse and industrial",
      "Multi-story buildings",
      "Commercial renovations"
    ],
    startingPrice: "$50,000",
    image: "/images/commercial-framing.jpg"
  },
  {
    icon: "🔨",
    title: "Custom Wood Structures",
    description: "Specialty framing for unique architectural elements, outdoor structures, and custom wood features that add character to any project.",
    features: [
      "Custom pergolas and gazebos",
      "Timber frame structures",
      "Decorative wood elements",
      "Outdoor living spaces",
      "Architectural features"
    ],
    startingPrice: "$8,000",
    image: "/images/custom-wood-structures.jpg"
  },
  {
    icon: "📐",
    title: "Project Consulting",
    description: "Expert consultation and planning services to ensure your framing project is designed and executed to the highest standards.",
    features: [
      "Project planning and design",
      "Structural engineering consultation",
      "Material selection guidance",
      "Timeline and budget planning",
      "Quality assurance oversight"
    ],
    startingPrice: "$2,500",
    image: "/images/project-consulting.jpg"
  },
  {
    icon: "🛠️",
    title: "Repair & Maintenance",
    description: "Professional repair and maintenance services for existing structures, ensuring longevity and structural integrity.",
    features: [
      "Structural repairs",
      "Damage assessment",
      "Preventive maintenance",
      "Emergency repairs",
      "Code compliance updates"
    ],
    startingPrice: "$3,000",
    image: "/images/repair-maintenance.jpg"
  },
  {
    icon: "📋",
    title: "Inspection Services",
    description: "Comprehensive inspection services for new construction, existing structures, and pre-purchase assessments.",
    features: [
      "New construction inspections",
      "Pre-purchase assessments",
      "Structural integrity checks",
      "Code compliance reviews",
      "Detailed reporting"
    ],
    startingPrice: "$500",
    image: "/images/inspection-services.jpg"
  }
];

const testimonials = [
  {
    name: "Sarah Johnson",
    role: "Homeowner",
    content: "The team at Rough Framing Co. transformed our vision into reality. Their attention to detail and quality craftsmanship exceeded our expectations.",
    rating: 5
  },
  {
    name: "Mike Rodriguez",
    role: "General Contractor",
    content: "I've worked with many framing crews, but Rough Framing Co. consistently delivers the highest quality work on time and within budget.",
    rating: 5
  },
  {
    name: "Jennifer Chen",
    role: "Property Developer",
    content: "Their commercial framing expertise helped us complete our office complex ahead of schedule. Professional, reliable, and exceptional quality.",
    rating: 5
  }
];

const Services: React.FC = () => {
  return (
    <div className="min-h-screen bg-black">
      <Helmet>
        <title>Services | Rough Framing Co. | Professional Framing Solutions</title>
        <meta name="description" content="Comprehensive framing services including residential, commercial, custom wood structures, consulting, and maintenance. Expert craftsmanship for all your framing needs." />
      </Helmet>

      {/* Hero Section */}
      <section className="relative h-[60vh] overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: "url(/images/services-hero.jpg)" }}
        >
          <div className="absolute inset-0 bg-black/60"></div>
        </div>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center text-white max-w-4xl mx-auto px-6">
            <h1 className="text-5xl md:text-7xl font-bold mb-6 tracking-tight">
              Our Services
            </h1>
            <p className="text-xl md:text-2xl mb-8 opacity-90 font-light text-amber-100">
              Professional framing solutions crafted with precision and expertise
            </p>
            <Link to="/contact">
              <button className="bg-amber-800 hover:bg-amber-700 text-white font-bold py-4 px-10 rounded-lg text-lg transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-4 focus:ring-amber-600/50 shadow-2xl border border-amber-600">
                Get Free Quote
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-24 bg-gray-900">
        <div className="container mx-auto px-6">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-6xl font-bold text-white mb-6 tracking-tight">
              Comprehensive Framing Solutions
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              From residential homes to commercial complexes, we provide expert framing services 
              tailored to your specific needs and project requirements.
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {services.map((service, index) => (
              <div
                key={index}
                className="bg-gray-800 rounded-2xl shadow-2xl hover:shadow-amber-900/20 transition-all duration-500 transform hover:-translate-y-2 border border-gray-700 hover:border-amber-600/30 overflow-hidden group"
              >
                <div className="h-64 bg-cover bg-center bg-no-repeat" style={{ backgroundImage: `url(${service.image})` }}>
                  <div className="h-full bg-gradient-to-t from-black/60 to-transparent"></div>
                </div>
                <div className="p-10">
                  <div className="flex items-center justify-between mb-6">
                    <div className="text-4xl">{service.icon}</div>
                    <div className="text-right">
                      <div className="text-sm text-gray-400">Starting at</div>
                      <div className="text-2xl font-bold text-amber-400">{service.startingPrice}</div>
                    </div>
                  </div>
                  <h3 className="text-2xl font-bold text-amber-400 mb-4 group-hover:text-amber-300 transition-colors">
                    {service.title}
                  </h3>
                  <p className="text-gray-300 mb-6 leading-relaxed">
                    {service.description}
                  </p>
                  <ul className="space-y-2 mb-8">
                    {service.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center text-gray-300">
                        <svg className="w-4 h-4 mr-3 text-amber-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <Link to="/contact">
                    <button className="w-full bg-amber-800 hover:bg-amber-700 text-white font-bold py-3 px-6 rounded-lg transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-4 focus:ring-amber-600/50 shadow-xl border border-amber-600">
                      Get Quote for {service.title}
                    </button>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 bg-black">
        <div className="container mx-auto px-6">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-6xl font-bold text-white mb-6 tracking-tight">
              Why Choose Rough Framing Co.?
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              Experience, expertise, and excellence in every project we undertake
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-amber-800 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-amber-400 mb-4">20+ Years Experience</h3>
              <p className="text-gray-300 leading-relaxed">
                Decades of expertise in framing construction across residential and commercial projects.
              </p>
            </div>
            
            <div className="text-center">
              <div className="bg-amber-800 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-amber-400 mb-4">On-Time Delivery</h3>
              <p className="text-gray-300 leading-relaxed">
                We pride ourselves on completing projects on schedule, every time.
              </p>
            </div>
            
            <div className="text-center">
              <div className="bg-amber-800 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-amber-400 mb-4">Premium Quality</h3>
              <p className="text-gray-300 leading-relaxed">
                Using only the finest materials and techniques to ensure lasting quality.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-gray-900">
        <div className="container mx-auto px-6">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-6xl font-bold text-white mb-6 tracking-tight">
              What Our Clients Say
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              Don't just take our word for it - hear from satisfied customers
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className="bg-gray-800 rounded-2xl p-8 shadow-2xl hover:shadow-amber-900/20 transition-all duration-500 transform hover:-translate-y-2 border border-gray-700 hover:border-amber-600/30"
              >
                <div className="flex mb-6">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <svg key={i} className="w-5 h-5 text-amber-400" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
                <p className="text-gray-300 mb-6 leading-relaxed italic">
                  "{testimonial.content}"
                </p>
                <div>
                  <div className="font-bold text-amber-400">{testimonial.name}</div>
                  <div className="text-gray-400 text-sm">{testimonial.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-black">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-6xl font-bold text-white mb-6 tracking-tight">
            Ready to Start Your Project?
          </h2>
          <p className="text-xl text-gray-300 mb-10 max-w-3xl mx-auto leading-relaxed">
            Get a free consultation and quote from our expert team. We'll work with you to 
            create the perfect framing solution for your needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <button className="bg-amber-800 hover:bg-amber-700 text-white font-bold py-4 px-10 rounded-lg text-lg transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-4 focus:ring-amber-600/50 shadow-2xl border border-amber-600">
                Request Free Quote
              </button>
            </Link>
            <a href="tel:+1234567890">
              <button className="bg-transparent hover:bg-gray-800 text-white font-bold py-4 px-10 rounded-lg text-lg transition-all duration-300 border-2 border-amber-600 hover:border-amber-500 focus:outline-none focus:ring-4 focus:ring-amber-600/50">
                Call (123) 456-7890
              </button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services; 